import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Code2, Zap, Braces, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const DeveloperSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const codeSnippet = `// Extract data from a document
const response = await fetch('https://api.smartocr.io/v1/extract', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    file_url: 'https://example.com/invoice.pdf',
    document_type: 'invoice'
  })
});

const { data } = await response.json();
console.log(data.vendor);  // "Acme Corp"
console.log(data.total);   // 1250.00`;

  const features = [
    {
      icon: Code2,
      title: 'Simple REST API',
      description: 'Clean endpoints with comprehensive documentation.',
    },
    {
      icon: Zap,
      title: 'Fast Processing',
      description: 'Average response time under 2 seconds.',
    },
    {
      icon: Braces,
      title: 'Structured Output',
      description: 'Clean JSON that integrates with any stack.',
    },
  ];

  return (
    <section
      ref={ref}
      id="docs"
      className="py-20 md:py-28 bg-[#0A2540]"
      data-testid="developer-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-1.5 bg-[#635BFF]/20 text-[#635BFF] text-sm font-medium rounded-full mb-6">
              For Developers
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Built for{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#635BFF] to-[#00D4FF]">
                Developers
              </span>
            </h2>
            <p className="text-lg text-white/70 leading-relaxed mb-8">
              Our API is designed to be simple yet powerful. Get started in minutes with our SDKs for JavaScript, Python, and more. Structured JSON output integrates seamlessly with your existing applications.
            </p>

            <div className="grid sm:grid-cols-3 gap-6 mb-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
                  className="text-center sm:text-left"
                >
                  <div className="w-12 h-12 bg-[#635BFF]/20 rounded-xl flex items-center justify-center mb-3 mx-auto sm:mx-0">
                    <feature.icon className="w-6 h-6 text-[#635BFF]" />
                  </div>
                  <h3 className="font-heading text-white font-semibold mb-1">{feature.title}</h3>
                  <p className="text-white/50 text-sm">{feature.description}</p>
                </motion.div>
              ))}
            </div>

            <Button
              className="bg-[#635BFF] hover:bg-[#5851E0] text-white rounded-full px-8 h-12 font-semibold transition-all duration-200 active:scale-95"
              data-testid="dev-docs-btn"
            >
              Read Documentation
            </Button>
          </motion.div>

          {/* Right content - Code block */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="bg-[#1E1E3F] rounded-2xl shadow-2xl overflow-hidden border border-white/10">
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-[#1E1E3F]">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                  <span className="ml-3 text-white/50 text-sm">extract.js</span>
                </div>
                <button
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors duration-200"
                  data-testid="copy-code-btn"
                >
                  <Copy className="w-4 h-4 text-white/50" />
                </button>
              </div>
              <pre className="p-6 overflow-x-auto">
                <code className="text-sm font-mono leading-relaxed">
                  {codeSnippet.split('\n').map((line, index) => {
                    // Color the code
                    let coloredLine = line;
                    coloredLine = coloredLine.replace(/(\/\/.*)$/g, '<span class="text-[#546E7A]">$1</span>');
                    coloredLine = coloredLine.replace(/(const|await|fetch|method|headers|body)/g, '<span class="text-[#C792EA]">$1</span>');
                    coloredLine = coloredLine.replace(/('https:\/\/[^']*'|'[^']*'|"[^"]*")/g, '<span class="text-[#C3E88D]">$1</span>');
                    coloredLine = coloredLine.replace(/(console\.log)/g, '<span class="text-[#82AAFF]">$1</span>');
                    coloredLine = coloredLine.replace(/(JSON\.stringify|response\.json)/g, '<span class="text-[#82AAFF]">$1</span>');

                    return (
                      <div key={index} className="text-white/90" dangerouslySetInnerHTML={{ __html: coloredLine || '&nbsp;' }} />
                    );
                  })}
                </code>
              </pre>
            </div>

            {/* Decorative elements */}
            <div className="absolute -z-10 -top-8 -right-8 w-32 h-32 bg-[#635BFF]/20 rounded-full blur-2xl" />
            <div className="absolute -z-10 -bottom-8 -left-8 w-24 h-24 bg-[#00D4FF]/20 rounded-full blur-2xl" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
